import React, { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface FeatureCardProps {
  icon?: ReactNode;
  title: string;
  description: string;
  iconContainerClassName?: string;
  className?: string;
}

export interface CallToActionProps {
  title: ReactNode | string;
  description?: string;
  features?: FeatureCardProps[];
  cta?: {
    label: string;
    href: string;
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
    size?: 'default' | 'sm' | 'lg' | 'icon';
    className?: string;
  };
  className?: string;
  backgroundClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  featuresContainerClassName?: string;
  children?: ReactNode;
}

const FeatureCard = ({ 
  icon, 
  title, 
  description, 
  iconContainerClassName = "rounded-full bg-blue-100 p-4 mb-4",
  className
}: FeatureCardProps) => {
  return (
    <Card className={cn("h-full", className)}>
      <CardContent className="p-6 flex flex-col h-full items-center text-center">
        {icon && (
          <div className={cn(iconContainerClassName)}>
            {icon}
          </div>
        )}
        <h3 className="text-xl font-bold mb-3">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </CardContent>
    </Card>
  );
};

const CallToAction = ({
  title,
  description,
  features,
  cta,
  className,
  backgroundClassName = "py-16 md:py-24 bg-gradient-to-br from-blue-50 to-gray-100",
  titleClassName = "text-3xl md:text-4xl font-bold mb-6",
  descriptionClassName = "text-xl text-gray-700 mb-12",
  featuresContainerClassName = "grid grid-cols-1 md:grid-cols-3 gap-6 mb-10",
  children
}: CallToActionProps) => {
  return (
    <section className={cn(backgroundClassName)}>
      <div className="container">
        <div className={cn("text-center max-w-4xl mx-auto", className)}>
          {typeof title === 'string' ? (
            <h2 className={cn(titleClassName)}>{title}</h2>
          ) : (
            title
          )}
          
          {description && (
            <p className={cn(descriptionClassName)}>
              {description}
            </p>
          )}
          
          {features && features.length > 0 && (
            <div className={cn(featuresContainerClassName)}>
              {features.map((feature, index) => (
                <FeatureCard
                  key={index}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  iconContainerClassName={feature.iconContainerClassName}
                  className={feature.className}
                />
              ))}
            </div>
          )}
          
          {cta && (
            <Button 
              asChild 
              size={cta.size || "lg"} 
              variant={cta.variant || "default"}
              className={cn("bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 text-lg", cta.className)}
            >
              <a href={cta.href}>{cta.label}</a>
            </Button>
          )}
          
          {children}
        </div>
      </div>
    </section>
  );
};

export default CallToAction; 